import { SymmKey } from "src/common/keys";
import { FSMessage, Node, FSRole, FSType } from "src/common/keys/protos/collections_pb";
import {
  Account, GenericListEntity, GenericEntryEntity, DriveEntryType, DriveEntryTypes, DirectoryPaginationInfo, WatchedTabs, dayjs, mapEntryType, Helpers,
  PermissionSetType, GrantSet, GrantSetType, getDirSymmKey, decryptItemName, PermissionSet, getEnumKey, DirectoryInfo, DriveItemType, decryptRootDirName
} from "src/common";



// Build the list of Watched and Starred Entries (since they use the same GenericNode structure)
export class GenericNodesList implements GenericListEntity {
  id: string = WatchedTabs.root; // default to Roots
  collection_id: string = WatchedTabs.root; // default to Roots
  entries!: GenericEntryEntity[];
  pagination_info!: DirectoryPaginationInfo;
  readonly _nodes!: FSMessage.GenericNode[];
  // NOTE: If Fetching Root nodes, (NodeIdentifier) id and collection_id  will default to WatchedTabs.root
  constructor(message: FSMessage) {
    this._nodes = message.getNodesList();
    this.pagination_info = {
      total_count: message.getTotalCount() || this._nodes.length,
      has_more: message.getHasMore(),
      next_seq: message.getNextSeq(),
      next_subseq: message.getNextSubseq(),
    };
    this.id = WatchedTabs.root;
    this.collection_id = WatchedTabs.root;
  }
  get item(): GenericListEntity {
    return {
      collection_id: this.collection_id,
      id: this.id,
      entries: this.entries,
      pagination_info: this.pagination_info,
    };
  }
}
export class GenericEntry implements GenericEntryEntity {
  id!: string;
  collection_id!: string;
  maintainer_id?: string;
  parent_id!: string;
  type!: DriveEntryTypes;
  name!: string;
  version!: string;
  last_modification_date!: string;
  is_deleted?: boolean;
  symmetric_key?: SymmKey;
  permissions?: PermissionSetType;
  grants?: GrantSetType;
  mapped_type?: DriveEntryTypes;
  type_class?: string;
  type_label?: string;

  constructor(entry: GenericEntryEntity) {
    Object.assign(this, entry);
  }

  get item(): GenericEntryEntity {
    return {
      id: this.id,
      collection_id: this.collection_id,
      maintainer_id: this.maintainer_id,
      parent_id: this.parent_id,
      type: this.type,
      name: this.name,
      version: this.version,
      last_modification_date: this.last_modification_date,
      is_deleted: this.is_deleted,
      symmetric_key: this.symmetric_key,
      permissions: this.permissions,
      grants: this.grants,
      type_label: this.type_label,
      mapped_type: this.mapped_type,
      type_class: this.type_class,
    };
  }

  // Description: Set full_path_list and last_modification_date for links (missing dates in the node: FSMessage.GenericNode)
  public setFullPathList(full_path_list?: FSMessage.FullNodePath) {
    if (!this.last_modification_date && !!full_path_list) {
      const default_path = full_path_list.getDefaultCollectionPathList();
      const node = default_path.slice().pop();
      if (!!node) {
        this.last_modification_date = GenericEntry.getNodeDate(node);
      }
    }
  }

  // Description: get date for the node from FSMessage.GenericNode
  static getNodeDate(node: FSMessage.GenericNode | FSMessage.Dir.PathNode): string {
    const _date = node.getLastUpdate();
    const latest_date = node.hasLastLocalFsUpdate() && dayjs(node.getLastLocalFsUpdate()).isValid() ? dayjs.utc(node.getLastLocalFsUpdate()).local() :
      !!_date ? dayjs.unix(_date) : undefined;
    return !!latest_date ? latest_date.format("MM/DD/YYYY h:mm A") : "";
  }

  static async init(
    node: FSMessage.GenericNode,
    current_account: Account,
    permissions: PermissionSetType,
    grants?: GrantSetType,
    parent_directory_info?: DirectoryInfo): Promise<GenericEntry> {
    const symmetric_key = await this.getActiveSymmKey(node, current_account, permissions, grants, parent_directory_info?.symmetric_key);
    const decoded_name = await this.decryptName(node, current_account, permissions, grants, symmetric_key) || "";
    const is_root = node.getMaintainerId_asB64() === node.getId_asB64() || (!grants && !node.getParentId_asB64());
    const type = is_root ? DriveEntryType.ACL_NODE : getEnumKey(FSType, node.getType()) as DriveEntryTypes;
    const entry_info = !!type ? mapEntryType(type, decoded_name) : null;
    const entry = {
      id: node.getId_asB64(),
      collection_id: node.getCollectionId_asB64(),
      maintainer_id: node.getMaintainerId_asB64(),
      parent_id: node.getParentId_asB64(),
      type,
      last_modification_date: this.getNodeDate(node),
      name: decoded_name || entry_info?.type_label || "",
      version: node.getVersion_asB64(),
      is_deleted: node.getIsDeleted(),
      permissions,
      grants,
      symmetric_key,
      // entry_info:
      type_label: entry_info?.type_label,
      mapped_type: entry_info?.mapped_type,
      type_class: entry_info?.type_class
    };
    return new GenericEntry(entry);
  }

  // Description: Get Correct symmetric key from node OR parent
  static async getActiveSymmKey(
    node: FSMessage.GenericNode,
    current_account: Account,
    permissions: PermissionSetType,
    grants?: GrantSetType,
    parent_symmetric_key?: SymmKey):
    Promise<SymmKey | undefined> {
    try {
      const key_version = node.getWrappedDirKey()?.getKeyVersion();
      const dir_symm_key = node.getWrappedDirKey();
      let symmetric_key = parent_symmetric_key; // Use parent symmetric_key for children directories
      if (!parent_symmetric_key) {
        const grant = !!grants ? GrantSet.getGrantByRole(grants, key_version, Node.ACLRole.READER) : undefined;
        const reader_user_key = !!grant ? await grant.key(current_account) : undefined;
        symmetric_key = !!dir_symm_key ? await getDirSymmKey(dir_symm_key, current_account, permissions, reader_user_key) : undefined;
      }
      return symmetric_key;
    } catch (m) {
      console.error(m);
    }
  }

  // Description:  decrypt regular names
  static async decryptName(node: FSMessage.GenericNode,
    current_account: Account,
    permissions: PermissionSetType,
    grants?: GrantSetType,
    symmetric_key?: SymmKey):
    Promise<string | undefined> {
    try {
      let decoded_name = "working...";
      if (!!grants && !!node.getMaintainerScopedName() || !!node.getRootDirName()) {
        decoded_name = await this.getDirectoryMaintainersName(node, current_account, permissions, grants) || "";
      } else {
        const key_version = node.getWrappedDirKey()?.getKeyVersion();
        const set = PermissionSet.init(permissions);
        const reader = await set.permission(FSRole.READER, key_version)?.key(current_account);
        decoded_name = !!symmetric_key ? await decryptItemName(node.getName_asU8(), symmetric_key, reader) : "";
      }
      return decoded_name;
    } catch (m) {
      console.error(m);
    }
  }

  // Description: Get Maintainers Name from FSMessage.WatchNode
  static async getDirectoryMaintainersName(node: FSMessage.GenericNode, current_account: Account, permissions: PermissionSetType, grant_set?: GrantSetType): Promise<string | undefined> {
    let name;
    const root_dir_name = node.getRootDirName();
    if (node.hasRootDirName() && !!root_dir_name) {
      const name_content = root_dir_name.getContent_asU8();
      const key_version = root_dir_name.getKeyVersion();
      name = await decryptRootDirName(name_content, permissions, current_account, key_version, { wrapped_name: node.getName_asU8(), key_version: node.getWrappedDirKey()?.getKeyVersion() });
    } else {
      const key_version = node.hasMaintainerScopedName() ? node.getMaintainerScopedName()?.getKeyVersion() : node.getWrappedDirKey()?.getKeyVersion();
      const name_content = node.hasMaintainerScopedName() ? node.getMaintainerScopedName()?.getContent_asU8() : undefined;
      const grant = !!grant_set ? GrantSet.getGrantByRole(grant_set, key_version) : undefined;
      const reader_user_key = !!grant ? await grant.key(current_account) : undefined;
      name = (!!reader_user_key && !!name_content) ? Helpers.utf8Decode(await reader_user_key.encryption_key.unseal(name_content)) : undefined;
    }
    return name;
  }

  // Description: Build an item to place hold the error row
  static initErrorItem(node?: FSMessage.GenericNode): GenericEntryEntity {
    if (!!node) {
      const is_root = node?.getMaintainerId_asB64() === node?.getId_asB64();
      const type = is_root ? DriveEntryType.ACL_NODE : getEnumKey(FSType, node.getType()) as DriveEntryTypes;
      const entry_info = !!type ? mapEntryType(type, "") : null;
      return {
        id: node.getId_asB64(),
        collection_id: node.getCollectionId_asB64(),
        maintainer_id: node.getMaintainerId_asB64(),
        parent_id: node.getParentId_asB64(),
        type,
        last_modification_date: this.getNodeDate(node),
        name: (type === DriveEntryType.FILE ? DriveItemType.WatchedFile : DriveItemType.WatchedFolder),
        version: node.getVersion_asB64(),
        type_label: entry_info?.type_label,
        mapped_type: entry_info?.mapped_type,
        type_class: entry_info?.type_class
      };
    } else {
      return {
        id: "",
        collection_id: "",
        parent_id: "",
        type: DriveEntryType.DIR,
        last_modification_date: "",
        name: DriveItemType.WatchedItem,
        version: ""
      };
    }
  }

}


